import Constants from 'js/Constants';
import { resetPageNumbers } from 'js/actions';
import SearchBar from 'js/components/MacroInput/SearchBar';
import CardsListPageNumbersContainer from 'js/components/elements/cards/CardsListPageNumbersContainer';
import {
  getCurrentSelectedOption,
  listSelectStyles,
  SelectInput,
  SelectOption,
} from 'js/components/elements/rselect/ReactSelectUtils';
import UnsortedSelect from 'js/components/elements/rselect/UnsortedSelect';
import { AvailableFilterTypesByView } from 'js/constants/visibilityFilters';
import { MacroOptionsContextWrap } from 'js/contexts/MacroOptionsContext';
import { areSegmentsDifferent } from 'js/lib/SearchFilterUtils';
import { pick, values } from 'lodash';
import React, { useEffect, useReducer, useRef } from 'react';
import { useSelector } from 'react-redux';
import useAppDispatch from 'js/hooks/useAppDispatch';
import classes from './SearchControls.module.scss';
import { useLocalAccessLevel } from 'new/hooks/useLocalAccessLevel';
import { useHandleAccessLevelChanges } from 'new/hooks/useHandleAccessLevelChanges';

const initialState = {
  currentPage: 1,
  totalPages: null,
  totalItems: null,
  sortCriteria: 'NAME_ASC',
};

function reducer(state, action) {
  switch (action.type) {
    case 'updateCurrentPage':
      return { ...state, currentPage: action.value };
    case 'updateTotalPages':
      return { ...state, totalPages: action.value };
    case 'updateTotalItems':
      return { ...state, totalItems: action.value };
    case 'updateSortCriteria':
      return { ...state, sortCriteria: action.value };
  }
  throw Error('Unknown action: ' + action.type);
}

const SearchControls = ({
  view,
  isLoading,
  loadData,
  onResourceSuggestionSelected = null,
  actionSlot = null,
}) => {
  const dispatch = useAppDispatch();
  const { filters: allFilters, availableFilters: allAvailableFilters } = useSelector(
    (state: any) => state.app.visibilityFilters,
  );
  const [state, searchDispatch] = useReducer(reducer, initialState);
  const prevFilters = useRef();
  const filters = allFilters[view];
  const availableFilterTypes = AvailableFilterTypesByView[view];
  const sortCriteriaOptions = values(Constants.SortCriteriaInfo);
  const selectedSortOption = getCurrentSelectedOption(sortCriteriaOptions, state.sortCriteria);

  useEffect(() => {
    dispatch(resetPageNumbers());
    loadListData();
  }, []);

  useEffect(() => {
    if (filters && prevFilters.current && areSegmentsDifferent(filters, prevFilters.current)) {
      loadListData();
    }

    prevFilters.current = filters;
  }, [filters]);

  /**
   * local access level configuration
   */
  const { aclQueryMode, ...accessControls } = useLocalAccessLevel();
  useHandleAccessLevelChanges(loadListData, [aclQueryMode]);

  function loadListData() {
    const availableFilters = pick(allAvailableFilters, availableFilterTypes);

    const thunk = loadData(
      state.currentPage,
      state.sortCriteria,
      availableFilters,
      false, // setGlobalPagination: boolean
      { aclQueryMode },
    );

    dispatch(thunk).then((response: { totalPages: number; totalItems: number }) => {
      searchDispatch({
        type: 'updateTotalPages',
        value: response.totalPages,
      });
      searchDispatch({
        type: 'updateTotalItems',
        value: response.totalItems,
      });
    });
  }

  const onChangeState = ({ type, value }) => {
    const availableFilters = pick(allAvailableFilters, availableFilterTypes);
    const { currentPage, sortCriteria } = state;
    searchDispatch({ type: type, value: value });

    // Root cause, confirmed live 2026-08-21: these two calls used to omit the
    // 5th `options` arg entirely. getReusableTransformListing then fell back
    // to the GLOBAL `state.app.accessControl.mode` instead of THIS picker's
    // own local `aclQueryMode` toggle (owned-by-me / accessible-to-me) — so a
    // page or sort change silently re-queried under whatever access mode some
    // unrelated page last set globally, not the mode currently selected here.
    // That's a real mechanism for both reported symptoms: the list looking
    // empty/wrong right after a page-1 load (mount fires with the correct
    // local aclQueryMode; this fired without it) and it varying by account/
    // session (each account's global accessControl.mode differs).
    if (type === 'updateCurrentPage') {
      dispatch(loadData(value, sortCriteria, availableFilters, false, { aclQueryMode }));
    } else if (type === 'updateSortCriteria') {
      dispatch(loadData(currentPage, value, availableFilters, false, { aclQueryMode }));
    }
  };

  const onSortCriteriaChanged = async (selectedOption) => {
    onChangeState({
      type: 'updateSortCriteria',
      value: selectedOption.value,
    });
  };

  return (
    <div className={classes.root}>
      <MacroOptionsContextWrap>
        <SearchBar
          view={view}
          availableFilterTypes={availableFilterTypes}
          onResourceSelected={onResourceSuggestionSelected}
          inModal
          accessLevelControlProps={{ controlled: true, ...accessControls }}
        />
      </MacroOptionsContextWrap>
      <UnsortedSelect
        options={sortCriteriaOptions}
        components={{ Input: SelectInput, Option: SelectOption }}
        isClearable={false}
        isSearchable
        backspaceRemovesValue={false}
        value={selectedSortOption}
        onChange={onSortCriteriaChanged}
        styles={listSelectStyles}
      />
      <CardsListPageNumbersContainer
        isLoading={isLoading}
        listing={state}
        onListingChange={onChangeState}
      />
      {actionSlot}
    </div>
  );
};

export default SearchControls;
